/**
 * Widget kind union — the FE↔BE contract for the SOLVE workspace.
 *
 * Kinds mirror `ui-elements/components/solves/workbench/widgetCatalog.ts`
 * (camelCase). Per-kind params schemas live in `@playbookai/widget-schemas`
 * (Zod source of truth); inferred TS types are re-exported there.
 *
 * Sticky notes are NOT a widget kind — they're a canvas-only Yjs path
 * (`solve-feature/canvas/notes.ts`). The reference toolbar has a separate
 * "Note" button alongside "+ Widget", reflecting that notes are free-form
 * annotations rather than entity-backed methodology widgets.
 *
 * Storage split: most kinds are SQL entity-backed (SOLVE workspace cards +
 * dashboard widgets). The Hoshin canvas kinds (`textCard` … `kpiCard`, PB-234)
 * are Yjs-native — their content lives inline in the canvas Y.Doc, they are
 * NOT entity-creatable, and they must NOT appear in the entity kind set or the
 * `GET /v1/widgets/kinds` catalogue. `WIDGET_STORAGE` in
 * `@playbookai/widget-schemas` is the single source of truth for that split;
 * `ENTITY_WIDGET_KINDS` (playbook-be) and the catalogue derive from it.
 */
export const WIDGET_KINDS = [
    // Specify
    "problemStatement",
    "tags",
    "runChart",
    "kpiTile",
    "problemTree",
    // Oppress
    "currentStateStats",
    "containmentCards",
    "problemTimeline",
    "containmentPlan",
    "exposureRiskReview",
    // Look
    "paretoL1",
    "paretoL2",
    "funnel",
    "causalChain",
    "fishbone",
    "processTrace",
    // Verify
    "impactEffort",
    "countermeasureTable",
    "stakeholderMap",
    // Ensure
    "controlPlan",
    "escalationMatrix",
    // Phase-agnostic SOLVE content widgets.
    "genericTable",
    "markdownText",
    // Shared entity-backed reference cards. Hoshin uses these instead of
    // changing the process-global Yjs `image` / `linkCard` kinds used by VSM.
    "imageTile",
    "linkTile",
    // VSM. The widget registry is module-agnostic; the SIPOC scoping board is the
    // first VSM entry here (distinct from `ATTACHED_WIDGET_KINDS` in `./vsm.ts`,
    // which are the VSM current-state canvas monitoring overlays). `goals` is the
    // VSM charter's baseline → target → % change table (VSM-WS10, PB-347).
    // `charter` is the authoritative persisted aggregate; it is unbound until
    // the VSM-owned artifact routes land in Wave 3.
    "sipoc",
    "goals",
    "charter",
    "projectContext",
    // Dashboard (Home / generic EntityDashboard — ADR-0015). Distinct from the
    // SOLVE canvas kinds above; `kpiTile` (count tile) and `singleKpi` (pinned
    // sparkline KPI) are intentionally separate widgets.
    "singleKpi",
    "chart",
    "kpiWatchlist",
    "kpiTable",
    "myTasks",
    "projectPulse",
    "strategicHealth",
    "dailyBrief",
    "coachingNudge",
    "moodCheck",
    "mySolves",
    "myVsms",
    "quickAccess",
    "kaizenTable",
    "kaizenHealth",
    // Cross-SOLVE action read model (Personal Home). Distinct from `myTasks`,
    // whose dataset is an unimplemented personal task feed: `solveActions`
    // projects the Containment Plan and Countermeasure Table content the SOLVE
    // canvas already owns, read-only, and is dashboard-instance-only.
    "solveActions",
    // Hoshin canvas Yjs-native content widgets (PB-234). storage: "yjs" — inline
    // in the canvas Y.Doc, not entity-creatable. See WIDGET_STORAGE.
    "textCard",
    "image",
    "linkCard",
    "kpiCard",
    // LAMA report dashboard widgets (ADR-0054): the eight content sections of the
    // campaign report as dashboard-instance kinds. Instance store is
    // `dashboard_instance` ONLY (`WIDGET_INSTANCE_STORES` in widget-schemas) —
    // registering them here must NOT make `POST /v1/widgets` accept them, so
    // `ENTITY_WIDGET_KINDS` deliberately excludes them.
    "lamaReportOverview",
    "lamaMaturityProfile",
    "lamaMaturityTrend",
    "lamaParticipationTrend",
    "lamaCategoryResults",
    // 2026-09-10 ADR-0027 amendment: the v3 hierarchy in percent, for both
    // report sources (finalised baseline, latest wave).
    "lamaMaturityHierarchy",
    "lamaHierarchyResults",
    // PB-191 D35: the pre-rename kind, kept resolvable through an EXPAND rather
    // than swapped, because Task 4's row `UPDATE` runs as a PostSync hook AFTER
    // the app is serving and Prisma has no down migrations. Retired by Task 33
    // once no environment holds a row with this literal.
    "lamaPillarResults",
    "lamaTeamHeatmap",
    "lamaTopMovers",
    "lamaPriorityRoadmap",
];
/**
 * SOLVE canvas widget kinds whose placement count is constrained per canvas.
 * Shared by the toolbar affordance and the authoritative placement service.
 */
export const SOLVE_WIDGET_PLACEMENT_LIMITS = {
    problemStatement: 1,
    currentStateStats: 1,
    exposureRiskReview: 1,
};
export function getSolveWidgetPlacementLimit(kind) {
    return SOLVE_WIDGET_PLACEMENT_LIMITS[kind];
}
/**
 * Widget kinds valid on the SOLVE canvas (`solve_canvas`) — the single shared
 * roster the FE canvas (`solve-feature/widgets/phase-mapping`), the placement
 * rail (`SolveWidgetPlacementAuthorizer.descriptor.allowedWidgetKinds`) and the
 * backend projection (`solve-canvas.doc-type`) all consume, so they cannot
 * drift. Same contract Hoshin gets from `HOSHIN_CANVAS_WIDGET_KINDS` below.
 *
 * This is the RENDERABLE roster, deliberately wider than the FE add-menu:
 * `currentStateStats` and `containmentCards` are legacy Oppress kinds kept
 * alive for existing Solves but absent from defaults and from "+ Widget".
 * Narrowing this to the addable list would reject their placements.
 *
 * An EXPLICIT scoped list, not a derived filter — the widget registry is
 * module-agnostic and other modules offer overlapping kinds (see
 * docs/learnings/2026-06-19-shared-widget-registry-canvas-scope.md).
 */
export const SOLVE_CANVAS_WIDGET_KINDS = [
    // Specify
    "problemStatement",
    "tags",
    "runChart",
    "kpiTile",
    "problemTree",
    // Oppress
    "problemTimeline",
    "containmentPlan",
    "exposureRiskReview",
    // Look
    "paretoL1",
    "paretoL2",
    "funnel",
    "causalChain",
    "fishbone",
    "processTrace",
    // Verify
    "impactEffort",
    "countermeasureTable",
    "stakeholderMap",
    // Ensure
    "controlPlan",
    "escalationMatrix",
    // Phase-agnostic.
    "genericTable",
    "markdownText",
    // Legacy Oppress kinds — renderable, never offered.
    "currentStateStats",
    "containmentCards",
];
/**
 * Widget kinds valid on the Hoshin canvas (`hoshin_canvas`) — the single shared
 * roster the FE add-menu (`hoshinWidgetCatalog`) and the backend projection
 * (`hoshin-canvas.doc-type`) both consume, so the two cannot drift (PB-278).
 *
 * This is an EXPLICIT scoped list, deliberately NOT a `WIDGET_STORAGE === "yjs"`
 * filter: VSM offers the same Yjs kinds, so a storage filter is not a per-canvas
 * roster (see docs/learnings/2026-06-19-shared-widget-registry-canvas-scope.md).
 * The `@playbookai/widget-schemas` invariant test pins every entry to a real
 * `WidgetKind` with `yjs` storage.
 */
export const HOSHIN_CANVAS_WIDGET_KINDS = [
    "textCard",
    "image",
    "linkCard",
    "kpiCard",
];
/**
 * PB-2914 target contract for migrating Hoshin's reusable canvas cards to the
 * entity-backed widget rail without changing the shared VSM Yjs roster.
 *
 * `kind` is the canonical SQL-backed kind Hoshin will place. `rendererKind`
 * identifies the already-shipped presentation body to reuse, so downstream
 * work adds no duplicate renderer. Image is deliberately excluded from this
 * active contract until the Document Service provides an approved hosted-image
 * origin; the legacy Yjs `image` kind remains available to VSM and old docs.
 */
export const HOSHIN_REUSABLE_WIDGET_TARGETS = {
    textCard: {
        kind: "markdownText",
        rendererKind: "markdownText",
        availability: "registered",
    },
    linkCard: {
        kind: "linkTile",
        rendererKind: "linkCard",
        availability: "registered",
    },
    kpiCard: {
        kind: "kpiTile",
        rendererKind: "kpiTile",
        availability: "registered",
    },
};
/**
 * Source-of-truth split for entity-backed widgets on both Hoshin plan Edit and
 * Workshop surfaces (ADR-0049). Values name ownership boundaries, not client
 * implementation details.
 */
export const HOSHIN_ENTITY_WIDGET_STORAGE_CONTRACT = {
    contentAndVersion: "widget.widgets",
    authoritativePlacement: "widget.placements",
    liveLayoutAndReference: "collab.documents",
};
//# sourceMappingURL=widgets.js.map